import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isDesktop } from '@westwing/ui-kit/breakPoints';
import { StateInterface } from 'Client/redux/types';
import { showOverlay } from 'Client/redux/header/overlays/actions';
import { getOverlayById } from 'Client/redux/header/overlays/selectors';
import { cookiesEnabled } from 'Common/helpers/environment';
import { ShopToClubConfigState } from 'Common/appshell_types/remoteConfigType';
import useConfig from 'Client/hooks/useConfig';
import useUserTracking from 'Client/hooks/flags/useUserTracking';
import useWasClubRegistrationViewed from 'Client/hooks/flags/useWasClubRegistrationViewed';
import useNeverShowShopToClub from 'Client/hooks/flags/useNeverShowShopToClub';
import { ShopToClubShownLabel } from 'Common/ga/shopToClub';
import useRouter from 'Client/hooks/useRouter';
import useBellaCookies, { oneDayTTL } from 'Client/hooks/useBellaCookies';

export const CLUB_REGISTRATION_COOKIE = 'was_club_registration_overlay_shown';

export enum PAGE_VIEW {
    firstPageView = 'first_page_view',
    secondPageView = 'second_page_view',
}

export interface ReduxStateProps {
    data: ShopToClubConfigState;
    isVisible: boolean;
}

export const SHOP_TO_CLUB_OVERLAY_SHOWN_IN_THIS_SESSION = 'ww_shop_to_club_overlay_shown_in_this_session';

const useClubRegistrationPopup = () => {
    const [hasBeenViewed, setHasBeenViewedStatus] = useWasClubRegistrationViewed();
    const [, setShopToClubOverlayShownInThisSession] = useBellaCookies(
        SHOP_TO_CLUB_OVERLAY_SHOWN_IN_THIS_SESSION,
        true
    );
    const dispatch = useDispatch();
    const { isReturningUser, isLoggedIn, didLoginFinishOnce, isRegistered } = useUserTracking();
    const { isAppWebviewPage } = useRouter();
    const config = useConfig();
    const [neverShowShopToClub] = useNeverShowShopToClub();

    const isActive = useSelector<StateInterface, boolean>(
        state => getOverlayById(state.header.overlays, config.shopToClubOverlayId).isActive
    );
    const data = useSelector<StateInterface, ShopToClubConfigState>(state => {
        return state.configurations.shopToClub;
    });

    const isDisabledFromBob = data && !data.isEnabled;
    let canShowOnThisPage = isReturningUser && !isActive && !hasBeenViewed;

    if (isDesktop() && data.pageViewLimit === PAGE_VIEW.firstPageView) {
        canShowOnThisPage = !isActive && !hasBeenViewed;
    }

    // We need to be sure we have requested login check, else we may show to logged in users
    const failConditions = [
        isRegistered,
        !didLoginFinishOnce,
        isAppWebviewPage,
        isLoggedIn,
        !data,
        isDisabledFromBob,
        !canShowOnThisPage,
        neverShowShopToClub,
    ];
    const skipRender = failConditions.find(condition => !!condition);

    useEffect(() => {
        const expirationInDays = data && data.expirationInDays;
        const cookieTTL = expirationInDays * 24 * 60 * 60 * 1000;
        const wasExpirationDisabled = !expirationInDays && hasBeenViewed;
        if (wasExpirationDisabled && cookiesEnabled) {
            setHasBeenViewedStatus(0, cookieTTL);
        }

        if (!skipRender && cookiesEnabled) {
            const mobileDelayMS = data.mobileDelayInSeconds * 1000;
            const overlayTimer = setTimeout(() => {
                dispatch(
                    showOverlay(config.shopToClubOverlayId, {
                        isLogin: false,
                        data: { trackingLabel: ShopToClubShownLabel.PageLoad },
                    })
                );
                if (cookieTTL) {
                    setHasBeenViewedStatus(1, cookieTTL);
                }
                setShopToClubOverlayShownInThisSession(1, oneDayTTL);
            }, mobileDelayMS);

            return () => clearTimeout(overlayTimer);
        }
        return () => {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canShowOnThisPage]);

    return !skipRender;
};

export default useClubRegistrationPopup;
